import { useRef, useState } from 'react';
import { importEft, type ImportedEft } from '../../lib/eft/aniCache';
import { tryParseEbtsRef } from '../../lib/workflows/ebtsRef';
import type { WorkflowConfig } from '../../lib/workflows/config';
import { parseName, parseEbtsDate } from '../../lib/workflows/eftBuilder';

/**
 * Starting a workflow from an EFT that already exists.
 *
 * The file is read on the backend, which is where the SDK lives: the demographics come back as Type-2
 * field numbers and the imagery is staged in an Ani cache, already converted, with thumbnails to draw.
 * Nothing is decoded in the browser.
 *
 * The Type-2 numbers are mapped onto form fields HERE rather than on the server, because which field
 * feeds which input is workflow configuration — runtime data the browser already holds. Mapping it
 * server-side would be a second copy that drifts the first time a site admin edits a workflow.
 */

interface Props {
  workflow: WorkflowConfig;
  onImported: (result: ImportedEft, demographics: Record<string, string>) => void;
}

/** Type-2 field number -> the demographic input it feeds, inverted from the workflow's own map. */
function mapDescriptive(workflow: WorkflowConfig, descriptive: Record<string, string>): Record<string, string> {
  /*
   * Fields the TARGET workflow sets as constants are never imported.
   *
   * A static Type-2 value is a property of the transaction type, not of the person: 2.037 (Reason
   * Fingerprinted) is "Firearms" for ATF and "DO 556-73 REQUEST" for DOCE. It is also in SHARED_MAP, so
   * inverting the fieldMap made it importable — and buildCustomEft applies mapped demographics AFTER the
   * statics, so an ATF file imported into a DOCE transaction overwrote DOCE's constant with "Firearms".
   * The submission was structurally valid, validated clean against EBTS 11.0, and the FBI rejected it.
   */
  /*
   * Both sides are compared as PARSED references, not as strings. The mapping now spells a Type-2 field
   * `2.037` while the statics and the imported payload use the bare number, so a string comparison would
   * silently stop excluding the statics — and the static this protects is 2.037 itself, whose leakage from
   * an ATF file into a DOCE transaction is what the FBI rejected.
   */
  const staticFields = new Set(
    Object.keys(workflow.staticType2Fields ?? {})
      .map((k) => tryParseEbtsRef(k)?.field)
      .filter((f): f is number => f !== undefined),
  );
  const byField = new Map<string, string>();
  for (const [name, ref] of Object.entries(workflow.fieldMap ?? {})) {
    if (typeof ref !== 'string') continue;
    const parsed = tryParseEbtsRef(ref);
    /*
     * Type-2 only. The payload is the file's descriptive record, flattened to field numbers — there is no
     * Type-1 in it, and importing another agency's ORI or destination into this transaction would be wrong
     * even if there were.
     */
    if (!parsed || parsed.record !== 2 || staticFields.has(parsed.field)) continue;
    byField.set(String(parsed.field), name);
  }
  /*
   * Decoded by the FIELD'S OWN TYPE rather than by name. EBTS writes a date as YYYYMMDD and a date
   * input needs YYYY-MM-DD, so 2.022 copied across verbatim rendered as an empty box. Keying off the
   * type covers every date a workflow defines — date of birth, date printed — instead of whichever one
   * happened to be reported.
   */
  const typeOf = new Map((workflow.fields ?? []).map((f) => [f.name, f.type]));
  const demo: Record<string, string> = {};
  for (const [field, value] of Object.entries(descriptive)) {
    const name = byField.get(field);
    // A field this workflow does not collect is left behind on purpose. It stays in the file it came
    // from; putting it somewhere the operator cannot see or correct would be worse than dropping it.
    if (!name) continue;
    demo[name] = typeOf.get(name) === 'date' ? parseEbtsDate(value) : value;
  }

  /*
   * T2_NAM (2.018) is COMPOSED, not mapped. `postRules.setName` builds `LAST,FIRST M SUFFIX` out of
   * four separate inputs on the way out, so no fieldMap entry reverses it and the loop above cannot see
   * it — which is why the subject's name arrived blank on the first import.
   *
   * Split back out only where the workflow has nothing already: a real mapping, if one exists, is more
   * specific than anything recovered from the composite.
   */
  if (workflow.postRules?.setName) {
    for (const [key, value] of Object.entries(parseName(descriptive['18'] ?? ''))) {
      if (value && !demo[key]) demo[key] = value;
    }
  }
  return demo;
}

export function ImportEft({ workflow, onImported }: Props) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);

  async function choose(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    setError(null);
    setSummary(null);
    try {
      const result = await importEft(file, workflow.captureType);
      const demo = mapDescriptive(workflow, result.descriptive);
      const mapped = Object.keys(demo).length;
      const total = Object.keys(result.descriptive).length;
      setSummary(
        `${result.images.length} impression(s) and ${mapped} of ${total} descriptive field(s) imported` +
          `${result.tot ? ` from a ${result.tot} transaction` : ''}.`,
      );
      onImported(result, demo);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That file could not be imported.');
    } finally {
      setBusy(false);
      // Cleared so choosing the same file again re-triggers change.
      if (input.current) input.current.value = '';
    }
  }

  return (
    <div className="import-eft">
      <div className="import-eft-row">
        <div>
          <div className="field-label">Start from an existing EFT</div>
          <p className="cart-note" style={{ textAlign: 'left', margin: '4px 0 0' }}>
            Reads the demographics and fingerprints out of a .eft file. Prints keep their original WSQ;
            anything this workflow does not collect is left in the source file.
          </p>
        </div>
        <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => input.current?.click()}>
          {busy ? 'Reading…' : 'Import .eft'}
        </button>
        <input
          ref={input}
          type="file"
          accept=".eft,.ebts,.nist,application/octet-stream"
          style={{ display: 'none' }}
          onChange={(e) => void choose(e.target.files?.[0])}
        />
      </div>
      {summary && <p className="import-eft-ok">{summary}</p>}
      {error && <p className="cart-error" style={{ marginTop: 8 }}>{error}</p>}
    </div>
  );
}
